import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { Tag } from '~/components/Tag';

import { ArtistAppliedFilterListItem } from './ArtistAppliedFilterListItem';

export type ArtistAppliedLabelFilter = {
  labelId: number;
  displayName: string;
  updatedAt?: string;
  updatedBy?: string;
};

interface ArtistAppliedFilterListProps {
  isCrossover: boolean;
  isOnRoster?: boolean;
  labelFilters: ArtistAppliedLabelFilter[];
  onRemoveCrossover: () => void;
  onRemoveOnRoster: () => void;
  onRemoveLabel: (labelId: number) => void;
  onClear: () => void;
  onMoreClick: () => void;
}

const MAX_VISIBLE_FILTERS = 3;

const ArtistAppliedFilterList = ({
  isCrossover,
  isOnRoster,
  labelFilters,
  onRemoveCrossover,
  onRemoveOnRoster,
  onRemoveLabel,
  onClear,
  onMoreClick,
}: ArtistAppliedFilterListProps) => {
  const { t } = useTranslation('roster');
  const visibleLabelFilterCount = MAX_VISIBLE_FILTERS - (isCrossover ? 1 : 0);

  return (
    <div
      className="flex items-center gap-md py-md"
      aria-label="Applied filters"
    >
      <div className="text-body-sm">{t('Filter(s) applied:')}</div>
      {isCrossover ? (
        <ArtistAppliedFilterListItem
          label={t('crossover.label')}
          onRemove={onRemoveCrossover}
        />
      ) : null}
      {isOnRoster !== undefined ? (
        <ArtistAppliedFilterListItem
          label={t(
            isOnRoster ? 'filters.onRoster.label' : 'filters.offRoster.label',
          )}
          onRemove={onRemoveOnRoster}
        />
      ) : null}
      {labelFilters
        .slice(0, visibleLabelFilterCount)
        .map(({ labelId, displayName, updatedAt, updatedBy }) => {
          const tooltipContent = updatedAt ? (
            <div className="text-body-xs font-semibold leading-tight">
              <p>
                {t('labels.lastUpdated')}:{' '}
                {new Date(updatedAt).toLocaleDateString()}
              </p>
              <p>
                {t('labels.by')}: {updatedBy}
              </p>
            </div>
          ) : null;
          return (
            <ArtistAppliedFilterListItem
              key={labelId}
              label={displayName}
              tooltipContent={tooltipContent}
              onRemove={() => onRemoveLabel(labelId)}
            />
          );
        })}
      {labelFilters.length > visibleLabelFilterCount ? (
        <Tag
          onClick={onMoreClick}
          variant="secondary"
          key="more"
          className="gap-xs pr-sm"
        >
          {t('labels.moreLink', {
            count: labelFilters.length - visibleLabelFilterCount,
          })}
        </Tag>
      ) : null}
      <Button
        variant="text"
        onClick={onClear}
        className="-ml-sm px-sm py-xs text-body-sm font-semibold"
      >
        {t('filters.clearFilters')}
      </Button>
    </div>
  );
};

ArtistAppliedFilterList.displayName = 'ArtistAppliedFilterList';

export { ArtistAppliedFilterList };
